import { Component, ElementRef, NgZone, OnDestroy, OnInit, computed, effect, signal, viewChild } from '@angular/core';

import { registerLocaleData } from '@angular/common';
import localePl from '@angular/common/locales/pl';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { AuthBagService } from './auth/auth-bag.service';
import { AuthService } from './auth/auth.service';
import { Permissions } from './common/enums/Permissions';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { NavbarBridgeService } from './common/navbar/navbar-bridge.service';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';

registerLocaleData(localePl);

export interface MenuItem {
	name: string;
	iconName?: string;
	routerLink?: string;
	showSubmenu?: boolean;
	permissions: boolean;
	children?: MenuItem[];
}

interface Theme {
	backgroundColor: string;
	color: string;
}

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
	readonly isAuthenticated$ = this.authBagService.authStatus$;

	readonly Permissions = Permissions;

	readonly isOnlyDispatcher = computed(() => {
		const enabledPermissions = Object.entries(this.auth.permissionsByCode()).filter((val) => val[1]);
		return enabledPermissions.length === 1 && enabledPermissions[0][0] === Permissions.DISPATCHER;
	});
	readonly OfferTransportKind = OfferTransportKind;

	readonly MENU_ITEMS = computed<MenuItem[]>(() => {
		return [
			{
				name: 'Zadania',
				routerLink: '/task',
				iconName: 'fas fa-tasks',
				showSubmenu: false,
				permissions: !this.isOnlyDispatcher(),
				children: [],
			},
			{
				name: 'Skrzynka odbiorcza',
				routerLink: '/thread',
				iconName: 'fas fa-envelope',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
				children: [],
			},
			{
				name: 'Leady',
				routerLink: null,
				iconName: 'far fa-address-book',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.LEAD_ACCESS],
				children: [
					{
						name: 'Indywidualne',
						routerLink: '/leads/individual',
						iconName: 'fas fa-user',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Grupowe',
						routerLink: '/leads/group',
						iconName: 'fas fa-users',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Transportowe',
						routerLink: '/transport-leads',
						iconName: 'fas fa-bus',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
				],
			},
			{
				name: 'Transport',
				routerLink: null,
				iconName: 'fas fa-bus',
				showSubmenu: false,
				permissions:
					this.auth.permissionsByCode()[Permissions.VEHICLES_CALENDAR_ACCESS] ||
					this.auth.permissionsByCode()[Permissions.CHECK_IN_ACCESS] ||
					this.auth.permissionsByCode()[Permissions.DEPARTURES_ACCESS] ||
					this.auth.permissionsByCode()[Permissions.VEHICLES_EVENTS_ACCESS] ||
					this.auth.permissionsByCode()[Permissions.PAYMENTS_RENTALS] ||
					this.auth.permissionsByCode()[Permissions.DISPATCHER] ||
					this.auth.permissionsByCode()[Permissions.VEHICLES_ACCESS],
				children: [
					{
						name: 'Kalendarz',
						routerLink: '/tourism-calendar',
						iconName: 'far fa-calendar-alt',
						showSubmenu: false,
						permissions:
							this.auth.permissionsByCode()[Permissions.VEHICLES_CALENDAR_ACCESS] ||
							this.auth.permissionsByCode()[Permissions.DISPATCHER],
						children: [],
					},
					{
						name: 'Odprawy',
						routerLink: '/tourism-briefing/departure',
						iconName: 'far fa-calendar-check',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CHECK_IN_ACCESS],
						children: [],
					},
					{
						name: 'Powroty',
						routerLink: '/tourism-briefing/return',
						iconName: 'fas fa-arrow-circle-left',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.DEPARTURES_ACCESS],
						children: [],
					},
					{
						name: 'Wydarzenia',
						routerLink: '/tourism-events',
						iconName: 'fa fa-border-all',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.VEHICLES_EVENTS_ACCESS],
						children: [],
					},
					{
						name: 'Terminy płatności wynajmy',
						routerLink: '/eventBusiness/table',
						iconName: 'far fa-money-bill-alt',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.PAYMENTS_RENTALS],
						children: [],
					},
					{
						name: 'Wpłaty wynajmy',
						routerLink: '/eventBusinessPayments',
						iconName: 'fas fa-hand-holding-usd',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.PAYMENTS_RENTALS],
						children: [],
					},
					{
						name: 'Pojazdy',
						routerLink: '/vehicles',
						iconName: 'fa fa-car',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.VEHICLES_ACCESS],
						children: [],
					},
					{
						name: 'Dokumenty',
						routerLink: '/vehicles/documents',
						iconName: 'far fa-file',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.VEHICLES_ACCESS],
						children: [],
					},
					{
						name: 'Połącz układy siedzeń',
						routerLink: '/connect-bus-layouts',
						iconName: 'fa fa-link',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.VEHICLES_ACCESS],
						children: [],
					},
				],
			},
			{
				name: 'Warsztat',
				routerLink: null,
				iconName: 'fa-solid fa-warehouse',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.WORKSHOP_ACCESS],
				children: [
					{
						name: 'Dodatki i potrącenia',
						routerLink: '/settlements/settlementBonus',
						iconName: 'fas fa-font',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
					{
						name: 'Terminy Dodatków i potrąceń',
						routerLink: '/settlements/settlementBonusTerm',
						iconName: 'fas fa-dollar-sign',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
				],
			},
			{
				name: 'TIR',
				routerLink: null,
				iconName: 'fa-solid fa-truck-front',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.TIR_ACCESS],
				children: [
					{
						name: 'Dodatki i potrącenia',
						routerLink: '/settlements/settlementBonus',
						iconName: 'fas fa-font',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
					{
						name: 'Terminy Dodatków i potrąceń',
						routerLink: '/settlements/settlementBonusTerm',
						iconName: 'fas fa-dollar-sign',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
				],
			},
			{
				name: 'TMS',
				routerLink: null,
				iconName: 'fa-solid fa-truck-moving',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.TMS_ACCESS],
				children: [
					{
						name: 'Giełdy i zlecenia',
						routerLink: '/tms/freight-search',
						iconName: 'fas fa-search',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
				],
			},
			{
				name: 'Kierowcy',
				routerLink: null,
				iconName: 'fas fa-user',
				showSubmenu: false,
				permissions:
					this.auth.permissionsByCode()[Permissions.DRIVER_ACCESS] ||
					this.auth.permissionsByCode()[Permissions.DRIVER_CALENDAR_ACCESS],
				children: [
					{
						name: 'Lista',
						routerLink: '/drivers',
						iconName: 'fa fa-list',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.DRIVER_ACCESS],
						children: [],
					},
					{
						name: 'Kalendarz',
						routerLink: '/drivers-calendar',
						iconName: 'far fa-calendar-alt',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.DRIVER_CALENDAR_ACCESS],
						children: [],
					},
					{
						name: 'Dodatki i potrącenia',
						routerLink: '/settlements/settlementBonus',
						iconName: 'fas fa-font',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
					{
						name: 'Terminy Dodatków i potrąceń',
						routerLink: '/settlements/settlementBonusTerm',
						iconName: 'fas fa-dollar-sign',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
					{
						name: 'Wynagrodzenia',
						routerLink: '/drivers/salary',
						iconName: 'fa fa-list',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
					{
						name: 'Dokumenty',
						routerLink: '/documentsList',
						iconName: 'far fa-file',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
				],
			},
			{
				name: 'Rezydent',
				routerLink: null,
				iconName: 'fab fa-avianex',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_ACCESS],
				children: [
					{
						name: 'Lista klientów',
						routerLink: '/residentCustomerList',
						iconName: 'fa fa-list',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_ACCESS],
						children: [],
					},
					{
						name: 'Baza wycieczek',
						routerLink: '/residentBaseTrips',
						iconName: 'fas fa-ship',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_TRIPS_DB_ACCESS],
						children: [],
					},
					{
						name: 'Sprzedane wycieczki',
						routerLink: '/residentSoldTrips',
						iconName: 'fas fa-campground',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_TRIPS_DB_ACCESS],
						children: [],
					},
					{
						name: 'Wycieczki',
						routerLink: '/resident/residentTrips',
						iconName: 'fas fa-clipboard-list',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_ACCESS],
						children: [],
					},
					{
						name: 'Sprzedaż',
						routerLink: '/residentTripsReservation/add',
						iconName: 'fas fa-cash-register',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_ACCESS],
						children: [],
					},
					{
						name: 'Rezerwacje',
						routerLink: '/residentTripReservationList',
						iconName: 'fas fa-ticket-alt',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_ACCESS],
						children: [],
					},
					{
						name: 'Kwaterunki',
						routerLink: '/accommodations',
						iconName: 'fas fa-campground',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_ACCESS],
						children: [],
					},
					{
						name: 'IsTour',
						routerLink: '/accommodations/istour',
						iconName: 'fas fa-atlas',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.RESIDENT_ACCESS],
						children: [],
					},
				],
			},
			{
				name: 'Piloci',
				routerLink: null,
				iconName: 'fas fa-user-secret',
				showSubmenu: false,
				permissions:
					this.auth.permissionsByCode()[Permissions.PILOTS_ACCESS] ||
					this.auth.permissionsByCode()[Permissions.PILOTS_CALENDAR_ACCESS],
				children: [
					{
						name: 'Lista',
						routerLink: '/touristGuides',
						iconName: 'fa fa-list',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.PILOTS_ACCESS],
						children: [],
					},
					{
						name: 'Kalendarz',
						routerLink: '/tourist-guides-calendar',
						iconName: 'far fa-calendar-alt',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.PILOTS_CALENDAR_ACCESS],
						children: [],
					},
					{
						name: 'Wynagrodzenia',
						routerLink: '/tourist-guides-salary',
						iconName: 'fa fa-money-bill-wave',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.PILOTS_ACCESS],
						children: [],
					},
				],
			},
			{
				name: 'Organizacje',
				routerLink: '/organization',
				iconName: 'fa-solid fa-sitemap',
				showSubmenu: false,
				permissions: !this.isOnlyDispatcher(),
				children: [],
			},
			{
				name: 'System rezerwacji',
				routerLink: null,
				iconName: 'fas fa-suitcase-rolling',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
				children: [
					{
						name: 'Produkty',
						routerLink: '/product',
						iconName: 'fas fa-atlas',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Grupy ofert',
						routerLink: '/groupOffer',
						iconName: 'fa-solid fa-layer-group',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Terminy',
						routerLink: '/terms',
						iconName: 'fa-solid fa-calendar-day',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Sezon',
						routerLink: '/transportTerm',
						iconName: 'fas fa-cloud-sun-rain',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Szablony transportów',
						routerLink: '/transport/template',
						iconName: 'fas fa-clone',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Vouchery',
						routerLink: '/vouchers',
						iconName: 'fas fa-ticket',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Promocje',
						routerLink: '/promotions',
						iconName: 'fas fa-percent',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Usługi',
						routerLink: '/service',
						iconName: 'fas fa-couch',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Plik kwaterunkowy',
						routerLink: '/accommodation-file',
						iconName: 'fas fa-campground',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Lista rezerwacji',
						routerLink: '/reservationList',
						iconName: 'fa-solid fa-table-list',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Lista zadań',
						routerLink: '/taskBag',
						iconName: 'fas fa-tasks',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Lista płatności',
						routerLink: '/paymentList',
						iconName: 'fas fa-file-invoice-dollar',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Terminy płatności',
						routerLink: '/paymentTermList',
						iconName: 'fas fa-calendar-check',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Generowanie ofert',
						routerLink: '/refresh',
						iconName: 'fas fa-sync-alt',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.ADMIN],
						children: [],
					},
				],
			},
			{
				name: 'Ust. systemu',
				routerLink: null,
				iconName: 'fas fa-wrench',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
				children: [
					{
						name: 'Lokalizacje',
						routerLink: '/location',
						iconName: 'fas fa-location-pin',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Słowniki',
						routerLink: '/codebook',
						iconName: 'fas fa-book',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Wycieczki fakultatywne',
						routerLink: '/optionalTrip',
						iconName: 'fas fa-map-marked',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Przystanki',
						routerLink: '/stop/' + OfferTransportKind.COACH,
						iconName: 'fas fa-bus-simple',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
					{
						name: 'Lotniska',
						routerLink: '/stop/' + OfferTransportKind.PLANE,
						iconName: 'fas fa-plane',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
						children: [],
					},
				],
			},
			{
				name: 'Oskar.com.pl',
				routerLink: null,
				iconName: 'fab fa-chrome',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.NEW_RESERVATION_SYSTEM],
				children: [
					{
						name: 'Menu',
						routerLink: '/www/menu',
						iconName: 'fas fa-tasks',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Baner',
						routerLink: '/www/banner',
						iconName: 'far fa-images',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Sekcje',
						routerLink: '/www/section',
						iconName: 'fa-solid fa-section',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Szablony opisów',
						routerLink: '/www/descriptionTemplate',
						iconName: 'fa-solid fa-comment',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Ogólne nagłówki',
						routerLink: '/www/descriptionMain',
						iconName: 'fa-solid fa-comments',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Stopka',
						routerLink: '/www/footer',
						iconName: 'fas fa-window-maximize',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
				],
			},
			{
				name: 'Rozliczenia',
				routerLink: null,
				iconName: 'fas fa-hand-holding-usd',
				showSubmenu: false,
				permissions:
					this.auth.permissionsByCode()[Permissions.BANK_ACCOUNTS] ||
					this.auth.permissionsByCode()[Permissions.CREDIT_CARDS] ||
					this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS] ||
					this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTIONS],
				children: [
					{
						name: 'Transakcje',
						routerLink: '/bankTransactions',
						iconName: 'fas fa-tasks',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTIONS],
						children: [],
					},
					{
						name: 'Przelewy',
						routerLink: '/bankTransactions/transfers',
						iconName: 'fas fa-piggy-bank',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTIONS],
						children: [],
					},
					{
						name: 'Konta bankowe',
						routerLink: '/bankAccounts',
						iconName: 'fas fa-university',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.BANK_ACCOUNTS],
						children: [],
					},
					{
						name: 'Karty płatnicze',
						routerLink: '/bankCards',
						iconName: 'fas fa-credit-card',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS],
						children: [],
					},
					{
						name: 'Rodzaje transakcji',
						routerLink: '/bankTransactionsTypes',
						iconName: 'fas fa-receipt',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
					{
						name: 'Auto nazwy',
						routerLink: '/bankTransactionsNames',
						iconName: 'fas fa-font',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
					{
						name: 'Importy',
						routerLink: '/import',
						iconName: 'fas fa-file-import',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.CREDIT_CARDS_TRANSACTION_KINDS],
						children: [],
					},
					{
						name: 'Alior status',
						routerLink: '/alior-status',
						iconName: 'fa-solid fa-money-bill',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Masowe płatności',
						routerLink: '/payment/mass_rebooking',
						iconName: 'fas fa-file-import',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
				],
			},
			{
				name: 'Korespondencja',
				routerLink: '/postIn',
				iconName: 'fas fa-mail-bulk',
				showSubmenu: false,
				permissions: !this.isOnlyDispatcher(),
				children: [],
			},
			{
				name: 'Kontakty Google',
				routerLink: '/googleContacts',
				iconName: 'fab fa-google',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.GOOGLE_CONTACTS],
				children: [],
			},
			{
				name: 'Analityka',
				routerLink: null,
				iconName: 'fa-solid fa-chart-line',
				showSubmenu: false,
				permissions: !this.isOnlyDispatcher(),
				children: [
					{
						name: 'Marketing',
						routerLink: '/analytics/marketing',
						iconName: 'fa-solid fa-ranking-star',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Import cen',
						routerLink: '/analytics/import-term-price',
						iconName: 'fas fa-file-import',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Dopłaty do przystanków',
						routerLink: '/analytics/stop-fee-update',
						iconName: 'fas fa-bus',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
				],
			},
			{
				name: 'Windykacja',
				routerLink: null,
				iconName: 'fa-solid fa-magnifying-glass-dollar',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.VINDICATION_ACCESS],
				children: [
					{
						name: 'Faktury',
						routerLink: '/vindication/invoices',
						iconName: 'fa-solid fa-file-invoice-dollar',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Kontrahenci',
						routerLink: '/vindication/contractors',
						iconName: 'fa-solid fa-building',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
				],
			},
			{
				name: 'Użytkownicy',
				routerLink: null,
				iconName: 'fas fa-user-tie',
				showSubmenu: false,
				permissions: this.auth.permissionsByCode()[Permissions.DRIVER_ACCESS],
				children: [
					{
						name: 'Lista',
						routerLink: '/users',
						iconName: 'fa fa-list',
						showSubmenu: false,
						permissions: true,
						children: [],
					},
					{
						name: 'Grupy',
						routerLink: '/usersGroups',
						iconName: 'fas fa-users',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.DRIVER_CALENDAR_ACCESS],
						children: [],
					},
					{
						name: 'Działy',
						routerLink: '/usersDepartments',
						iconName: 'far fa-building',
						showSubmenu: false,
						permissions: this.auth.permissionsByCode()[Permissions.DEPARTMENTS_ACCESS],
						children: [],
					},
				],
			},
			{
				name: 'Wyloguj',
				routerLink: '/logout',
				iconName: 'fa fa-sign-out-alt',
				showSubmenu: false,
				permissions: true,
				children: [],
			},
		];
	});

	readonly NAVBAR_THEMES: Theme[] = [
		{ backgroundColor: '#2e3f52', color: '#ffffff' },
		{
			backgroundColor: '#FFFFFF',
			color: '#1E1E1E',
		},
		{
			backgroundColor: '#1E1E1E',
			color: '#FFFFFF',
		},
		{
			backgroundColor: '#0A3749',
			color: '#E0F7FA',
		},
		{
			backgroundColor: '#FF5722',
			color: '#FFF3E0',
		},
		{
			backgroundColor: '#121858',
			color: '#E8EAF6',
		},
		{
			backgroundColor: '#37474F',
			color: '#ECEFF1',
		},
		{
			backgroundColor: '#FFF8E1',
			color: '#5D4037',
		},
		{
			backgroundColor: '#F6B8B8',
			color: '#3B2F2F',
		},
	];

	readonly selectedTheme = signal<Theme>(this.NAVBAR_THEMES[0]);

	private prevUrl: string;
	private readonly sideNav = viewChild<MatSidenav>('snav');
	private readonly sidenavContentEl = viewChild(MatSidenavContent, { read: ElementRef<HTMLElement> });
	private readonly destroy$ = new Subject<void>();

	constructor(
		readonly auth: AuthService,
		readonly bridge: NavbarBridgeService,
		private readonly authBagService: AuthBagService,
		private readonly router: Router,
		private readonly ngZone: NgZone,
	) {
		this.authBagService.triggerTokenRefresh$.next();
		effect(() => {
			if (!this.sidenavContentEl()) {
				return;
			}
			const host = this.sidenavContentEl().nativeElement;
			const handler = () => this.closeSideNav();
			host.addEventListener('mousedown', handler);
			return () => host.removeEventListener('mousedown', handler);
		});
	}

	ngOnInit(): void {
		if (
			!(
				(
					window.location.pathname === '/' ||
					this.authBagService.isAuthRelatedUrl(window.location.href) ||
					window.location.origin.includes('localhost:9876')
				) // Need to add this so tests can pass
			)
		) {
			this.prevUrl = window.location.href.replace(window.location.origin, '');
		}

		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd),
				takeUntil(this.destroy$),
			)
			.subscribe((event: NavigationEnd) => {
				if (this.authBagService.isAuthRelatedUrl(event.url)) {
					return;
				}
				this.closeSideNav();
				this.prevUrl = event.url;
			});

		this.authBagService.authStatus$.pipe(takeUntil(this.destroy$)).subscribe((isUserAuthenticated) =>
			// ngZone.run is to be sure that this code will happen synchronously.
			// Without that there will be an error with routing after logging in and when end of session
			this.ngZone.run(async () => {
				const token = this.authBagService.tokenGet();

				if (!isUserAuthenticated && token) {
					await this.authBagService.erpAuthenticate(token);
				}

				if (!this.authBagService.authStatus$.getValue()) {
					this.router.navigate(['/login']);
					return;
				}

				if (!isUserAuthenticated) {
					return;
				}

				// If user is dispatcher he can't see any other page
				if (this.auth.rightsGet().length === 1 && this.auth.rightsGet()[0] === Permissions.DISPATCHER) {
					this.router.navigate(['tourism-calendar']);
					return;
				}

				if (this.prevUrl) {
					this.router.navigateByUrl(this.prevUrl);
					return;
				}

				this.router.navigate(['/task']);
			}),
		);
		const storedTheme = localStorage.getItem('navbarTheme');
		if (!!storedTheme) {
			this.selectedTheme.set(JSON.parse(storedTheme));
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	toggle(node: MenuItem, event?: MouseEvent): void {
		event?.stopPropagation();
		node.showSubmenu = !node.showSubmenu;
	}

	changeTheme(theme: Theme): void {
		this.selectedTheme.set(theme);
		localStorage.setItem('navbarTheme', JSON.stringify(theme));
	}

	private closeSideNav() {
		if (!!this.sideNav()?.opened) {
			this.sideNav().toggle();
		}
	}
}
